import { NavigationItem } from "../types"
import { CalendarEventType } from "../types/mainModulTypes"
import React from "react";
import dayjs from "dayjs";

// NOTE why?
const webspaceKey = "website"
export const rearrangeNavigationDataFromSulu = (res: any) => {
    const navItemsFromSulu = res?._embedded?.items
    if (!navItemsFromSulu) return
    const loadNavItemsWithChildren = (items: any[]) => {
        const navigationItems: NavigationItem[] = []
        for (const item of items) {
            if (item?.webspaceKey && item.webspaceKey !== webspaceKey) continue
            const navigationItem: NavigationItem = {
                id: item.id,
                title: item.title,
                url: item.url,
                children: [],
            }
            if (item.children) {
                navigationItem.children = loadNavItemsWithChildren(item.children)
            }
            navigationItems.push(navigationItem)
        }
        return navigationItems
    }
    const navigation = loadNavItemsWithChildren(navItemsFromSulu)
    return navigation
}

// convert hex-code in hsl-code
export const hexToHsl = (hex: string) => {
    let r = 0,
        g = 0,
        b = 0
    if (hex.length === 7) {
        // #RRGGBB
        r = parseInt(hex.substring(1, 3), 16)
        g = parseInt(hex.substring(3, 5), 16)
        b = parseInt(hex.substring(5, 7), 16)
    } else if (hex.length === 4) {
        // #RGB
        r = parseInt(hex[1] + hex[1], 16)
        g = parseInt(hex[2] + hex[2], 16)
        b = parseInt(hex[3] + hex[3], 16)
    }
    r /= 255
    g /= 255
    b /= 255
    let max = Math.max(r, g, b),
        min = Math.min(r, g, b)
    let h,
        s,
        l = (max + min) / 2
    if (max === min) {
        h = 0
        s = 0
        l = Math.round(l * 100)
    } else {
        let d = max - min
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0)
                break
            case g:
                h = (b - r) / d + 2
                break
            case b:
                h = (r - g) / d + 4
                break
        }
        h = Math.round((h ?? 0) * 60)
        s = Math.round(s * 100)
        l = Math.round(l * 100)
    }
    return `${h} ${s}% ${l}%` // Tailwind expects HSL without "hsl()"
}

// text to speach
export const speakOutLoud = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.lang = "de-DE" // Set the language to German
    speechSynthesis.speak(utterance)
}

// Helper function to recursively extract text from React elements
const extractTextFromElement = (element: any): string => {
    if (typeof element === 'string') {
        return element
    }
    
    if (typeof element === 'number') {
        return element.toString()
    }
    
    if (Array.isArray(element)) {
        return element.map(extractTextFromElement).join(' ')
    }
    
    if (element && typeof element === 'object') {
        // Handle React elements with props.children
        if (element.props) {
            if (element.props.children) {
                return extractTextFromElement(element.props.children)
            }
            // Handle self-closing elements or elements without children
            return ''
        }
        
        // Handle plain objects that might have text properties
        if (element.data) {
            return element.data
        }
        
        // Try to find any string values in the object
        const textParts: string[] = []
        for (const key in element) {
            if (element.hasOwnProperty(key)) {
                const value = element[key]
                if (typeof value === 'string' || typeof value === 'number') {
                    textParts.push(value.toString())
                } else if (value && typeof value === 'object') {
                    textParts.push(extractTextFromElement(value))
                }
            }
        }
        return textParts.join(' ')
    }
    
    return ''
}

// text to speach from richtext
export const speakOutLoudRichtext = (text: any) => {
    const textToSpeak = extractTextFromElement(text)
    
    if (textToSpeak && textToSpeak.trim()) {
        const utterance = new SpeechSynthesisUtterance(textToSpeak)
        utterance.lang = "de-DE" // Set the language to German
        speechSynthesis.speak(utterance)
    }
}

// stop speech to text
export const stopSpeechToText = () => {
    speechSynthesis.cancel()
}

// get Text Color from Module
export const getTextAndHeadingClasses = (modul: any, primary: string): string => {
    if (!("modulSettingHeadingColor" in modul) && !("modulSettingTextColor" in modul)) {
        return ""
    }
    const colorKeyQuote = Object.keys(modul).find((key) => key.includes("SettingQuote"))
    const textClass = modul?.modulSettingTextColor ? "text-accent" : "text-neutral"
    const headlineClass = modul?.modulSettingHeadingColor ? "headline-white" : "headline-primary"
    document.documentElement.style.setProperty("--quote", colorKeyQuote && modul[colorKeyQuote] ? "#f9f9f9" : "")
    return `${textClass} ${headlineClass} `
}

// get Text Color from Hero
export const getTextAndHeadingClassesHero = (hero: any): string => {
    const colorKeyHeading = Object.keys(hero).find((key) => key.includes("SettingHeadingColor"))
    const colorKeyText = Object.keys(hero).find((key) => key.includes("SettingTextColor"))

    const headlineClass = colorKeyHeading && hero[colorKeyHeading] ? "headline-white" : "headline-primary"
    const textClass = colorKeyText && hero[colorKeyText] ? "text-accent" : "text-neutral"

    return `${textClass} ${headlineClass}`
}



export function orderCalendarEventsByDate(events: CalendarEventType[]) {
    return events.sort((a, b) => new Date(a["calenderArticleDate"]).getTime() - new Date(b["calenderArticleDate"]).getTime())
}

// find next dated event
export function findNextDatedCalendarEvent(events: CalendarEventType[]) {
    const today = new Date()

    return (
        events
            .map((item, index) => ({ index, diff: new Date(item["calenderArticleDate"]).getTime() - today.getTime() }))
            .filter((item) => item.diff > 0)
            .sort((a, b) => a.diff - b.diff)
            .map((item) => item.index)[0] ?? -1
    )
}

// Check Enddate
export function handleEndDate(events: CalendarEventType[]): CalendarEventType[] {
    return events.flatMap(event => {
      if (event.calenderArticleDate2) {
        const startDate = dayjs(event.calenderArticleDate);
        const endDate = dayjs(event.calenderArticleDate2);
        // Unterschied in vollen Tagen (inklusive Start- und Enddatum)
        const daysDiff = endDate.diff(startDate, "day");
  
        // Erstellen eines Arrays mit jedem Tag vom Startdatum bis Enddatum (inklusive)
        return Array.from({ length: daysDiff + 1 }, (_, i) => ({
          ...event,
          // Ersetzen des Datums mit dem fortlaufenden Datum
          calenderArticleDate: startDate.add(i, "day").format("YYYY-MM-DD"),
          calenderArticleDate2: undefined,
        }));
      } else {
        return event;
      }
    });
  }


// get Crop Image Position
export function getCropPosition(position?: string) {
    if (!position) return ""

    const classes: string[] = []

    if (position.includes("Top")) classes.push("-top-10")
    if (position.includes("Bottom")) classes.push("-bottom-10")
    if (position.includes("Left")) classes.push("left-28")
    if (position.includes("Right")) classes.push("right-28")
    if (position.includes("Mid")) classes.push("left-1/2 -translate-x-1/2")

    return classes.join(" ")
}
